import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { insertItem, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { tap } from 'rxjs';
import { ApiResponse } from 'src/app/core/models/api-response';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { IdName } from 'src/app/shared/models/id-name';
import { redirectAndBypassUnsavedDataCheck } from 'src/app/shared/functions/redirect-and-bypass-unsaved-data-check';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import { MenuListLogicService } from 'src/app/core/services/menu-list-logic.service';
import { AdminUsersRoutes, UserUsersAndPaxRoutes } from 'src/app/core/enums/routes.enum';
import { IPax } from '../models/pax.model';
import { PaxApiService } from '../service/pax-api.service';
import {
  CreatePax,
  DeletePax,
  LoadAllPaxes,
  LoadPaxById,
  LoadPaxes,
  UpdatePax,
  ClearCurrentPax,
} from './pax.actions';

export interface PaxStateModel {
  paxes: IPax[];
  allPaxes: IdName[];
  paxesTotalPages: number;
  currentPax: IPax;
}

const PAX_STATE_TOKEN = new StateToken<PaxStateModel>('paxes');

@State<PaxStateModel>({
  name: PAX_STATE_TOKEN,
  defaults: {
    paxes: [],
    allPaxes: [],
    paxesTotalPages: 0,
    currentPax: <IPax>{},
  },
})
@Injectable()
export class PaxState {
  constructor(
    private service: PaxApiService,
    private menuListLogicService: MenuListLogicService
  ) {}

  @Action(LoadPaxes, { cancelUncompleted: true })
  loadPaxes({ getState, setState }: StateContext<PaxStateModel>, { filter }: LoadPaxes) {
    return this.service.getPaxes(filter).pipe(
      tap((response: ApiResponse<IPax[]>) => {
        const state = getState();
        setState({
          ...state,
          paxes: response.data,
          paxesTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadAllPaxes, { cancelUncompleted: true })
  loadAllPaxes(ctx: StateContext<PaxStateModel>) {
    return this.service.getAllPaxes().pipe(
      tap((allPaxes: IdName[]) => {
        ctx.setState(patch({ allPaxes }));
      })
    );
  }

  @Action(LoadPaxById, { cancelUncompleted: true })
  loadPaxById({ getState, setState }: StateContext<PaxStateModel>, { id }: LoadPaxById) {
    return this.service.getPaxById(id).pipe(
      tap((response: IPax) => {
        const state = getState();
        setState({
          ...state,
          currentPax: response,
        });
      })
    );
  }

  @Action(CreatePax, { cancelUncompleted: true })
  @OnSuccess({ message: 'paxCreated' })
  createPax(ctx: StateContext<PaxStateModel>, { pax }: CreatePax) {
    return this.service.createPax(pax).pipe(
      tap((data: IPax) => {
        ctx.setState(
          patch({
            paxes: insertItem<IPax>(data),
          })
        );
        const redirectUrl = this.menuListLogicService.isCurrentUrlInAdmin()
          ? AdminUsersRoutes.Paxes
          : UserUsersAndPaxRoutes.Paxes;
        redirectAndBypassUnsavedDataCheck(redirectUrl);
      })
    );
  }

  @Action(UpdatePax, { cancelUncompleted: true })
  @OnSuccess({ message: 'paxUpdated' })
  updatePax(ctx: StateContext<PaxStateModel>, { pax }: UpdatePax) {
    return this.service.updatePax(pax).pipe(
      tap(() => {
        ctx.setState(
          patch({
            paxes: updateItem<IPax>(
              (paxToUpdate: IPax | undefined) => paxToUpdate?.id === pax.id,
              pax
            ),
          })
        );
        const redirectUrl = this.menuListLogicService.isCurrentUrlInAdmin()
          ? AdminUsersRoutes.Paxes
          : UserUsersAndPaxRoutes.Paxes;
        redirectAndBypassUnsavedDataCheck(redirectUrl);
      })
    );
  }

  @Action(DeletePax, { cancelUncompleted: true })
  @OnSuccess({ message: 'paxDeleted', dispatch: ResetCurrentFilter })
  deletePax(ctx: StateContext<PaxStateModel>, { paxId }: DeletePax) {
    return this.service.deletePax(paxId).pipe(
      tap(() => {
        ctx.setState(
          patch({
            paxes: removeItem<IPax>((paxToRemove: IPax | undefined) => paxToRemove?.id === paxId),
          })
        );
        const redirectUrl = this.menuListLogicService.isCurrentUrlInAdmin()
          ? AdminUsersRoutes.Paxes
          : UserUsersAndPaxRoutes.Paxes;
        redirectAndBypassUnsavedDataCheck(redirectUrl);
      })
    );
  }

  @Action(ClearCurrentPax, { cancelUncompleted: true })
  clearCurrentPax(ctx: StateContext<PaxStateModel>) {
    ctx.setState(patch({ currentPax: undefined }));
  }

  @Selector()
  static paxesTotalPages(state: PaxStateModel) {
    return state.paxesTotalPages;
  }

  @Selector()
  static getPaxes(state: PaxStateModel) {
    return state.paxes;
  }

  @Selector()
  static getAllPaxes(state: PaxStateModel): IdName[] {
    return state.allPaxes;
  }

  @Selector()
  static getPaxById(state: PaxStateModel) {
    return state.currentPax;
  }
}
